import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Config, { priceForLanguage } from '../../lib/Config'
import { Language } from '../../lib/Enums/Language'
import { Budget } from '../../lib/Types'
import Headline from '../Elements/Headline'
import Text from '../Elements/Text'
import Paywall from '../Paywall'
import { SheetType } from './lib/Enums/SheetType'
import { isPaymentOverdue } from './lib/Payment'

interface Props {
  sheet: Budget
  show: boolean
}

// The article this links to is translated on WWW under the same
// translationKey ("hvad-er-et-budget"), but each locale has its own domain
// and slug — see Guide.tsx for the same pattern.
const WELCOME_BUDGET_ARTICLE_SLUG: Record<Language, string> = {
  [Language.DANISH]: 'hvad-er-et-budget',
  [Language.GERMAN]: 'was-ist-ein-budget',
  [Language.ENGLISH]: 'what-is-a-budget',
}

const WelcomeBudget: FC<Props> = ({ sheet, show }) => {
  const { t, i18n } = useTranslation(['budget', 'common'])
  const [showModal, setShowModal] = useState(show)
  const price = priceForLanguage(Config.PRICE_BUDGET, i18n.language)
  const overdue = isPaymentOverdue(sheet)

  return (
    <Paywall
      show={showModal}
      onHide={() => setShowModal(false)}
      sheetType={SheetType.BUDGET}
      sheetId={sheet.id}
      name={sheet.name || t('common:paywall.budget')}
      twoStep={!overdue}
      secondaryButtonLabel={
        overdue
          ? undefined
          : t(
            sheet.is_free
              ? 'budget:welcomeBudget.getStartedButton'
              : 'budget:welcomeBudget.startButton',
          )
      }
    >
      {overdue
        ? (
          <>
            <Headline>{t('budget:welcomeBudget.trialEndedTitle')}</Headline>
            <Text className='my-6'>
              {t('budget:welcomeBudget.trialEndedDescription', { price })}
            </Text>
          </>
        )
        : (
          <>
            <Headline>
              {t(
                sheet.is_free
                  ? 'budget:welcomeBudget.freeTitle'
                  : 'budget:welcomeBudget.title',
              )}
            </Headline>
            <Text className='my-6'>
              {t('budget:welcomeBudget.description')}{' '}
              <a
                className='underline'
                href={`${Config.WWW_DOMAIN[i18n.language as Language] ?? Config.WWW_DOMAIN[Language.DANISH]}/budget/${WELCOME_BUDGET_ARTICLE_SLUG[i18n.language as Language] ?? WELCOME_BUDGET_ARTICLE_SLUG[Language.DANISH]}`}
                target='_blank'
                rel='noreferrer'
              >
                {t('budget:welcomeBudget.linkText')}
              </a>
              .
            </Text>
            <video controls width='100%'>
              <source
                src={`${Config.WWW_DOMAIN[i18n.language as Language] ?? Config.WWW_DOMAIN[Language.DANISH]}/videos/opret-budget.mp4`}
                type='video/mp4'
              />
            </video>
            <Text className='my-6'>
              {sheet.is_free
                ? t('budget:welcomeBudget.freePricingText', { price })
                : t('budget:welcomeBudget.pricingText')}
            </Text>
          </>
        )}
    </Paywall>
  )
}

export default WelcomeBudget
